import {
  array,
  mixed,
  number,
  object,
  SchemaOf,
  string,
  StringSchema,
} from 'yup';

import {
  INVALID_NAMESPACE_NAME_MESSAGE,
  INVALID_NAMESPACE_PATTERN_MESSAGE,
  isValidNamespaceName,
  isValidNamespacePattern,
  sanitizeNamespaceName,
} from '../../../utils/namespace';

import {
  MAX_CONCURRENT_SCANS,
  MIN_CONCURRENT_SCANS,
  MIN_RESCAN_INTERVAL_HOURS,
  NAMESPACE_SCOPE_MODES,
  NamespaceScopeMode,
  RESCAN_INTERVAL_UNIT_HOURS,
  RescanIntervalUnit,
  isTrivySeverity,
  toRescanIntervalHours,
  VulnerabilityScanningFormValues,
} from './types';

// Only the list matching the active mode is validated; the other is a draft the
// user may never submit.
function namespaceListSchema(activeMode: NamespaceScopeMode) {
  const allowsPatterns = activeMode === 'all-except';

  return array(string().required())
    .required()
    .test('namespace-list', 'Invalid namespace', function validate(namespaces) {
      if (this.parent.namespaceScopeMode !== activeMode) {
        return true;
      }

      const list = namespaces ?? [];
      if (!allowsPatterns && list.length === 0) {
        return this.createError({
          message: 'At least one namespace is required in this mode',
        });
      }

      const seen = new Set<string>();
      for (let index = 0; index < list.length; index += 1) {
        const namespace = sanitizeNamespaceName(list[index] ?? '');

        if (seen.has(namespace)) {
          return this.createError({
            path: `${this.path}[${index}]`,
            message: `"${namespace}" is already listed`,
          });
        }
        seen.add(namespace);

        const isValid = allowsPatterns
          ? isValidNamespacePattern(namespace)
          : isValidNamespaceName(namespace);

        if (!isValid) {
          return this.createError({
            path: `${this.path}[${index}]`,
            message: allowsPatterns
              ? `Namespace pattern ${INVALID_NAMESPACE_PATTERN_MESSAGE}`
              : `Namespace ${INVALID_NAMESPACE_NAME_MESSAGE}`,
          });
        }
      }

      return true;
    });
}

export function createVulnerabilityScanningValidationSchema(
  nameValidation: StringSchema<string>
): SchemaOf<VulnerabilityScanningFormValues> {
  return object({
    name: nameValidation,
    environmentGroupIds: array(number().required()),
    rescanIntervalValue: number()
      .required('Rescan interval is required')
      .integer('Rescan interval must be a whole number')
      .min(1, 'Rescan interval must be at least 1')
      .test(
        'above-minimum',
        `Rescan interval must be at least ${MIN_RESCAN_INTERVAL_HOURS} hour`,
        function validate(value) {
          const unit = this.parent.rescanIntervalUnit as RescanIntervalUnit;
          if (value === undefined || !RESCAN_INTERVAL_UNIT_HOURS[unit]) {
            return true;
          }
          return (
            toRescanIntervalHours(value, unit) >= MIN_RESCAN_INTERVAL_HOURS
          );
        }
      ),
    rescanIntervalUnit: mixed<RescanIntervalUnit>()
      .oneOf(
        Object.keys(RESCAN_INTERVAL_UNIT_HOURS) as Array<RescanIntervalUnit>
      )
      .required(),
    maxConcurrentScans: number()
      .required('Concurrent scan limit is required')
      .integer('Concurrent scan limit must be a whole number')
      .min(
        MIN_CONCURRENT_SCANS,
        `Concurrent scan limit must be at least ${MIN_CONCURRENT_SCANS}`
      )
      .max(
        MAX_CONCURRENT_SCANS,
        `Concurrent scan limit must be ${MAX_CONCURRENT_SCANS} or fewer`
      ),
    severities: array(string().required())
      .required()
      .min(1, 'Select at least one severity to record')
      .test('known-severities', 'Unrecognised severity', (value) =>
        (value ?? []).every(isTrivySeverity)
      ),
    targetNamespace: string()
      .required('Namespace is required')
      .test('valid-namespace', function validate(value) {
        if (isValidNamespaceName(sanitizeNamespaceName(value ?? ''))) {
          return true;
        }
        return this.createError({
          message: `Namespace ${INVALID_NAMESPACE_NAME_MESSAGE}`,
        });
      }),
    namespaceScopeMode: mixed<NamespaceScopeMode>()
      .oneOf([...NAMESPACE_SCOPE_MODES])
      .required(),
    excludedNamespaces: namespaceListSchema('all-except'),
    includedNamespaces: namespaceListSchema('only'),
  });
}
